// Mobile-first SEO optimization utilities

export interface MobileViewport {
  width: number;
  height: number;
  devicePixelRatio: number;
  orientation: 'portrait' | 'landscape';
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export interface MobileSEOConfig {
  viewport: {
    width: string;
    initialScale: number;
    maximumScale: number;
    userScalable: boolean;
  };
  appleTouchIcon: {
    sizes: string;
    href: string;
  }[];
  manifest: {
    name: string;
    shortName: string;
    description: string;
    startUrl: string;
    display: 'standalone' | 'minimal-ui' | 'fullscreen' | 'browser';
    backgroundColor: string;
    themeColor: string;
    icons: Array<{
      src: string;
      sizes: string;
      type: string;
      purpose?: string;
    }>;
  };
  ampEnabled: boolean;
  mobileSpecificMeta: Record<string, string>;
}

export interface ResponsiveImage {
  src: string;
  srcSet: string;
  sizes: string;
  alt: string;
  width: number;
  height: number;
  loading: 'lazy' | 'eager';
  decoding: 'async' | 'sync' | 'auto';
}

/**
 * Detect device type and viewport information
 */
export const detectMobileViewport = (): MobileViewport => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const devicePixelRatio = window.devicePixelRatio || 1;
  const orientation = width > height ? 'landscape' : 'portrait';
  
  // Device type detection based on viewport width
  const isMobile = width <= 768;
  const isTablet = width > 768 && width <= 1024;
  const isDesktop = width > 1024;

  return {
    width,
    height,
    devicePixelRatio,
    orientation,
    isMobile,
    isTablet,
    isDesktop,
  };
};

/**
 * Generate mobile-optimized meta tags
 */
export const generateMobileMeta = (config: Partial<MobileSEOConfig> = {}): Record<string, string> => {
  const viewport = config.viewport || {
    width: 'device-width',
    initialScale: 1,
    maximumScale: 5,
    userScalable: true,
  };

  const meta: Record<string, string> = {
    // Essential mobile viewport
    'viewport': `width=${viewport.width}, initial-scale=${viewport.initialScale}, maximum-scale=${viewport.maximumScale}, user-scalable=${viewport.userScalable ? 'yes' : 'no'}`,
    
    // Mobile browser UI
    'mobile-web-app-capable': 'yes',
    'apple-mobile-web-app-capable': 'yes',
    'apple-mobile-web-app-status-bar-style': 'black-translucent',
    
    // Prevent automatic phone number detection
    'format-detection': 'telephone=no',
    
    // Microsoft mobile
    'msapplication-tap-highlight': 'no',
    'msapplication-TileColor': config.manifest?.themeColor || '#000000',
    
    // Theme colors
    'theme-color': config.manifest?.themeColor || '#000000',
    'msapplication-navbutton-color': config.manifest?.themeColor || '#000000',
  };

  // Add custom mobile-specific meta tags
  if (config.mobileSpecificMeta) {
    Object.assign(meta, config.mobileSpecificMeta);
  }

  return meta;
};

/**
 * Generate responsive image attributes
 */
export const generateResponsiveImage = (
  baseSrc: string,
  alt: string,
  sizes: Array<{ width: number; density?: number }> = [
    { width: 320 },
    { width: 640 },
    { width: 1024 },
    { width: 1920 }
  ]
): ResponsiveImage => {
  // Generate srcSet for different screen sizes
  const srcSet = sizes
    .map(size => {
      const density = size.density || 1;
      const filename = baseSrc.replace(/\.(jpg|jpeg|png|webp)$/i, `_${size.width}w.$1`);
      return density === 1 ? `${filename} ${size.width}w` : `${filename} ${density}x`;
    })
    .join(', ');

  // Generate sizes attribute for responsive behavior
  const sizesAttr = [
    '(max-width: 320px) 100vw',
    '(max-width: 640px) 100vw',
    '(max-width: 1024px) 50vw',
    '33vw'
  ].join(', ');

  return {
    src: baseSrc,
    srcSet,
    sizes: sizesAttr,
    alt,
    width: sizes[sizes.length - 1].width,
    height: Math.round(sizes[sizes.length - 1].width * 0.6), // Assume 16:10 aspect ratio
    loading: 'lazy',
    decoding: 'async',
  };
};

/**
 * Generate mobile-specific structured data
 */
export const generateMobileStructuredData = (
  baseData: any,
  mobileEnhancements: {
    mobileUrl?: string;
    appLinks?: {
      ios?: string;
      android?: string;
    };
    localBusiness?: {
      telephone?: string;
      address?: any;
    };
  } = {}
): any => {
  const mobileData = { ...baseData };

  // Add mobile-specific URL if different
  if (mobileEnhancements.mobileUrl) {
    mobileData.url = mobileEnhancements.mobileUrl;
  }

  // Add app links for mobile apps
  if (mobileEnhancements.appLinks) {
    mobileData.potentialAction = mobileData.potentialAction || [];
    
    if (mobileEnhancements.appLinks.ios) {
      mobileData.potentialAction.push({
        '@type': 'ViewAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': mobileEnhancements.appLinks.ios,
          'inLanguage': 'en',
          'actionPlatform': [
            'http://schema.org/IOSPlatform'
          ]
        }
      });
    }

    if (mobileEnhancements.appLinks.android) {
      mobileData.potentialAction.push({
        '@type': 'ViewAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': mobileEnhancements.appLinks.android,
          'inLanguage': 'en',
          'actionPlatform': [
            'http://schema.org/AndroidPlatform'
          ]
        }
      });
    }
  }

  // Enhance local business data for mobile
  if (mobileEnhancements.localBusiness && mobileData['@type'] === 'LocalBusiness') {
    if (mobileEnhancements.localBusiness.telephone) {
      mobileData.telephone = mobileEnhancements.localBusiness.telephone;
    }
    if (mobileEnhancements.localBusiness.address) {
      mobileData.address = mobileEnhancements.localBusiness.address;
    }
  }

  return mobileData;
};

/**
 * Generate Web App Manifest
 */
export const generateWebAppManifest = (config: MobileSEOConfig['manifest']): string => {
  const manifest = {
    name: config.name,
    short_name: config.shortName,
    description: config.description,
    start_url: config.startUrl,
    display: config.display,
    background_color: config.backgroundColor,
    theme_color: config.themeColor,
    icons: config.icons,
    orientation: 'portrait-primary',
    categories: ['business', 'productivity'],
    lang: 'en',
    dir: 'ltr',
  };

  return JSON.stringify(manifest, null, 2);
};

/**
 * Optimize content for mobile readability
 */
export const optimizeContentForMobile = (content: string): {
  optimizedContent: string;
  changes: string[];
} => {
  const changes: string[] = [];
  let optimizedContent = content;

  // Break up long paragraphs (mobile users prefer shorter paragraphs)
  const longParagraphs = content.match(/<p[^>]*>([^<]{200,})<\/p>/g);
  if (longParagraphs) {
    longParagraphs.forEach(paragraph => {
      const text = paragraph.replace(/<\/?p[^>]*>/g, '');
      const sentences = text.split(/(?<=[.!?])\s+/);
      
      if (sentences.length > 3) {
        const midPoint = Math.ceil(sentences.length / 2);
        const firstHalf = sentences.slice(0, midPoint).join(' ');
        const secondHalf = sentences.slice(midPoint).join(' ');
        
        const newParagraphs = `<p>${firstHalf}</p>\n\n<p>${secondHalf}</p>`;
        optimizedContent = optimizedContent.replace(paragraph, newParagraphs);
        changes.push('Split long paragraph for better mobile readability');
      }
    });
  }

  // Add mobile-friendly spacing
  optimizedContent = optimizedContent.replace(
    /<h([1-6])([^>]*)>/g,
    '<h$1$2 class="mobile-heading">'
  );
  changes.push('Added mobile-friendly heading classes');

  // Optimize lists for mobile
  optimizedContent = optimizedContent.replace(
    /<ul([^>]*)>/g,
    '<ul$1 class="mobile-list">'
  );
  optimizedContent = optimizedContent.replace(
    /<ol([^>]*)>/g,
    '<ol$1 class="mobile-list">'
  );
  changes.push('Added mobile-friendly list classes');

  return { optimizedContent, changes };
};

/**
 * Generate mobile-specific CSS for SEO elements
 */
export const generateMobileCSS = (): string => {
  return `
/* Mobile-first SEO optimizations */
@media (max-width: 768px) {
  .mobile-heading {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    line-height: 1.2;
    margin-bottom: 1rem;
    word-wrap: break-word;
  }
  
  .mobile-list {
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .mobile-list li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
  
  /* Optimize images for mobile */
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1rem auto;
  }
  
  /* Mobile-friendly tables */
  table {
    width: 100%;
    overflow-x: auto;
    display: block;
    white-space: nowrap;
  }
  
  /* Touch-friendly buttons */
  button, .button, a.btn {
    min-height: 44px;
    min-width: 44px;
    padding: 12px 16px;
    font-size: 16px; /* Prevents zoom on iOS */
  }
  
  /* Form optimization */
  input, textarea, select {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 12px;
    border-radius: 4px;
  }
  
  /* Navigation optimization */
  nav {
    position: sticky;
    top: 0;
    z-index: 1000;
  }
  
  /* Content spacing */
  .content {
    padding: 1rem;
    line-height: 1.6;
  }
  
  /* Mobile-specific hiding */
  .desktop-only {
    display: none !important;
  }
}

/* Tablet optimizations */
@media (min-width: 769px) and (max-width: 1024px) {
  .mobile-only {
    display: none !important;
  }
}

/* Desktop optimizations */
@media (min-width: 1025px) {
  .mobile-only {
    display: none !important;
  }
}
`;
};

/**
 * Check mobile SEO compliance
 */
export const checkMobileSEOCompliance = (
  document: Document
): {
  score: number;
  issues: string[];
  recommendations: string[];
} => {
  const issues: string[] = [];
  const recommendations: string[] = [];
  let score = 100;

  // Check viewport meta tag
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (!viewportMeta) {
    issues.push('Missing viewport meta tag');
    score -= 20;
  } else {
    const content = viewportMeta.getAttribute('content') || '';
    if (!content.includes('width=device-width')) {
      issues.push('Viewport meta tag missing width=device-width');
      score -= 10;
    }
    if (!content.includes('initial-scale=1')) {
      issues.push('Viewport meta tag missing initial-scale=1');
      score -= 5;
    }
  }

  // Check for mobile-friendly font sizes
  const smallText = document.querySelectorAll('*').length;
  if (smallText > 0) {
    // This would need actual computed styles in a real implementation
    recommendations.push('Ensure text is at least 16px for mobile readability');
  }

  // Check for touch-friendly elements
  const buttons = document.querySelectorAll('button, a, input[type="button"], input[type="submit"]');
  if (buttons.length > 0) {
    recommendations.push('Ensure interactive elements are at least 44px in height and width');
  }

  // Check for horizontal scrolling
  recommendations.push('Test for horizontal scrolling on mobile devices');

  // Check for mobile-specific structured data
  const structuredData = document.querySelectorAll('script[type="application/ld+json"]');
  if (structuredData.length === 0) {
    recommendations.push('Add structured data optimized for mobile search results');
  }

  // Check for app store links
  const appLinks = document.querySelectorAll('a[href*="apps.apple.com"], a[href*="play.google.com"]');
  if (appLinks.length === 0) {
    recommendations.push('Consider adding app store links if you have mobile apps');
  }

  return {
    score: Math.max(0, score),
    issues,
    recommendations,
  };
};

/**
 * Generate AMP (Accelerated Mobile Pages) metadata
 */
export const generateAMPMetadata = (
  canonicalUrl: string,
  ampUrl: string
): Record<string, string> => {
  return {
    'amp-canonical': canonicalUrl,
    'amp-html': ampUrl,
  };
};

/**
 * Optimize images for mobile Core Web Vitals
 */
export const optimizeImagesForMobile = (
  images: NodeListOf<HTMLImageElement>
): {
  optimizations: string[];
  criticalImages: HTMLImageElement[];
} => {
  const optimizations: string[] = [];
  const criticalImages: HTMLImageElement[] = [];

  images.forEach((img, index) => {
    // Mark above-the-fold images as critical
    const rect = img.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      criticalImages.push(img);
      img.loading = 'eager';
      optimizations.push(`Image ${index + 1}: Set to eager loading (above fold)`);
    } else {
      img.loading = 'lazy';
      optimizations.push(`Image ${index + 1}: Set to lazy loading`);
    }

    // Add decoding optimization
    img.decoding = 'async';

    // Check for missing alt text
    if (!img.alt) {
      optimizations.push(`Image ${index + 1}: Missing alt text`);
    }

    // Check for missing dimensions
    if (!img.width || !img.height) {
      optimizations.push(`Image ${index + 1}: Missing width/height attributes`);
    }
  });

  return { optimizations, criticalImages };
};